/**
 * Display units. Every volume in the app is held in litres (the cloud's
 * decilitres ÷ 10) and converted only at the point of display, so switching
 * systems is a re-render, never a refetch.
 */

export type UnitSystem = 'metric' | 'us' | 'uk'

export interface DisplayUnit {
  suffix: string
  /** Multiply a base value (litres, or litres/min) by this for display. */
  scale: number
  decimals: number
}

export const UNIT_SYSTEMS: { id: UnitSystem; label: string; title: string }[] = [
  { id: 'metric', label: 'L', title: 'Litres and cubic metres' },
  { id: 'us', label: 'gal (US)', title: 'US gallons — 3.785 L' },
  { id: 'uk', label: 'gal (UK)', title: 'Imperial gallons — 4.546 L' },
]

const LITRES_PER_GALLON: Record<Exclude<UnitSystem, 'metric'>, number> = {
  us: 3.785411784,
  uk: 4.54609,
}

const KEY = 'ir-units'
let current: UnitSystem = readStored()

function readStored(): UnitSystem {
  try {
    const v = localStorage.getItem(KEY)
    return v === 'us' || v === 'uk' ? v : 'metric'
  } catch {
    return 'metric'
  }
}

export function getUnitSystem(): UnitSystem {
  return current
}

export function setUnitSystem(system: UnitSystem): void {
  current = system
  try {
    localStorage.setItem(KEY, system)
  } catch {
    /* private mode — holds for this page view */
  }
}

/** Litres → the display unit for a chart whose largest value is `maxLitres`. */
export function volumeUnit(maxLitres: number, system: UnitSystem = current): DisplayUnit {
  if (system === 'metric') {
    if (maxLitres >= 10_000) return { suffix: 'm³', scale: 0.001, decimals: maxLitres >= 1_000_000 ? 0 : 1 }
    return { suffix: 'L', scale: 1, decimals: maxLitres >= 100 ? 0 : 1 }
  }
  const perGal = LITRES_PER_GALLON[system]
  const maxGal = maxLitres / perGal
  // Thousands of gallons past 10,000 — the scale water bills use.
  if (maxGal >= 10_000) return { suffix: 'kgal', scale: 1 / (perGal * 1000), decimals: maxGal >= 1_000_000 ? 0 : 1 }
  return { suffix: 'gal', scale: 1 / perGal, decimals: maxGal >= 100 ? 0 : 1 }
}

/** Litres per minute → the display unit for a flow-rate chart. */
export function flowRateUnit(maxLpm: number, system: UnitSystem = current): DisplayUnit {
  if (system === 'metric') return { suffix: 'L/min', scale: 1, decimals: maxLpm >= 100 ? 0 : 1 }
  const perGal = LITRES_PER_GALLON[system]
  return { suffix: 'gal/min', scale: 1 / perGal, decimals: maxLpm / perGal >= 100 ? 0 : 1 }
}

/** What a base quantity is, so the right display unit can be chosen. */
export type Dimension = 'volume' | 'flowrate' | 'raw'

export function displayUnit(dimension: Dimension, maxBase: number, rawSuffix = ''): DisplayUnit {
  if (dimension === 'volume') return volumeUnit(maxBase)
  if (dimension === 'flowrate') return flowRateUnit(maxBase)
  return { suffix: rawSuffix, scale: 1, decimals: 1 }
}
